import { useMemo, useState } from "react"
import { Link, useParams } from "react-router-dom"
import {
	Check,
	CircleCheck,
	CircleDot,
	Copy,
	Gauge,
	Mail,
	ListChecks,
	MessageSquare,
	MoreHorizontal,
	Paperclip,
	Plus,
	Search,
	Share2,
	Trash2,
	UserRound
} from "lucide-react"
import { api } from "../api/client"
import { useApp } from "../state/AppState"
import { indexTags, useProject } from "../state/useProject"
import {
	Avatar,
	Button,
	Card,
	EmptyState,
	Input,
	Popover,
	Select,
	Spinner,
	StatusBadge,
	TagChip,
	ToggleGroup,
	cx
} from "../components/ui"
import { SplitBar } from "../components/charts/StatTile"
import { copyText, relativeTime, plural } from "../lib/format"
import { IssueComposer } from "../components/issues/IssueComposer"
import { TeamStrip } from "./ProjectTeamPage"
import { ShareDialog } from "../components/issues/ShareDialog"
import { DeleteProjectDialog } from "../components/issues/DeleteProjectDialog"
import { NotFoundPage } from "./NotFoundPage"

export function ProjectIssuesPage() {
	const { projectId } = useParams()
	const { project, tags, setTags, issues, teams, peopleById, loading, error, afterMutation } =
		useProject(projectId)
	const { toast, timezone, user } = useApp()

	const [statusFilter, setStatusFilter] = useState("open")
	const [tagFilter, setTagFilter] = useState("all")
	const [assignedFilter, setAssignedFilter] = useState("all")
	const [search, setSearch] = useState("")
	const [composing, setComposing] = useState(false)
	const [sharing, setSharing] = useState(false)
	const [deleting, setDeleting] = useState(false)
	const [busyId, setBusyId] = useState(null)

	const tagsById = useMemo(() => indexTags(tags), [tags])

	const visible = useMemo(() => {
		const needle = search.trim().toLowerCase()
		return issues.filter((issue) => {
			if (statusFilter !== "all" && issue.status !== statusFilter) return false
			if (assignedFilter === "me" && issue.assignee_id !== user?.user_id) return false
			if (assignedFilter === "unassigned" && issue.assignee_id) return false
			if (tagFilter === "untagged" && (issue.tag_ids || []).length) return false
			if (tagFilter !== "all" && tagFilter !== "untagged") {
				if (!(issue.tag_ids || []).includes(tagFilter)) return false
			}
			if (needle && !issue.title.toLowerCase().includes(needle)) return false
			return true
		})
	}, [issues, statusFilter, tagFilter, assignedFilter, search, user])

	async function toggleStatus(issue) {
		setBusyId(issue.issue_id)
		const next = issue.status === "open" ? "closed" : "open"
		try {
			await api.setIssueStatus(projectId, issue.issue_id, next)
			await afterMutation()
			toast(
				next === "closed" ? `Resolved #${issue.number}` : `Reopened #${issue.number}`,
				next === "closed" ? "success" : "info"
			)
		} catch (cause) {
			toast(cause.message, "error")
		} finally {
			setBusyId(null)
		}
	}

	if (error?.status === 404) return <NotFoundPage />
	if (loading && !project) return <Spinner label="Loading project" />
	if (!project) return null

	const openCount = issues.filter((i) => i.status === "open").length
	const closedCount = issues.length - openCount
	const assignedToMe = issues.filter(
		(i) => i.assignee_id === user?.user_id && i.status === "open"
	).length

	return (
		<div className="space-y-5">
			<header className="flex flex-wrap items-start justify-between gap-3">
				<div className="min-w-0">
					<h1 className="text-xl font-semibold tracking-tight text-ink-900">
						{project.name}
					</h1>
					{project.description && (
						<p className="mt-1 max-w-2xl text-[0.8125rem] leading-relaxed text-ink-500">
							{project.description}
						</p>
					)}
					{!project.is_owner && !project.is_member && (
						<p className="mt-1.5 text-2xs text-ink-500">
							You are seeing the {plural(issues.length, "issue")} you were invited to.
						</p>
					)}
					<div className="mt-2.5">
						<TeamStrip projectId={projectId} project={project} teams={teams} />
					</div>
				</div>
				<div className="flex items-center gap-2">
					<Button icon={Share2} onClick={() => setSharing(true)}>
						Share thread
					</Button>
					{project.is_member && (
						<Button
							variant="primary"
							icon={Plus}
							onClick={() => setComposing(true)}
							disabled={project.can_raise_issue === false}
							title={
								project.can_raise_issue === false
									? "This project has reached its issue limit"
									: undefined
							}
						>
							New issue
						</Button>
					)}
					{project.is_owner && (
						<>
							{/* Tucked behind a menu rather than sat beside the
							    two buttons people reach for every day. */}
							<Popover
								className="w-56"
								trigger={({ toggle, open }) => (
									<Button
										icon={MoreHorizontal}
										onClick={toggle}
										aria-expanded={open}
										aria-label="Project options"
										className="px-2"
									/>
								)}
							>
								{({ close }) => (
									<button
										type="button"
										onClick={() => {
											close()
											setDeleting(true)
										}}
										className="flex w-full items-center gap-2.5 rounded-lg px-2.5 py-2 text-left text-[0.8125rem] text-rose-700 transition-colors hover:bg-rose-50"
									>
										<Trash2 size={15} className="shrink-0" aria-hidden />
										Delete project
									</button>
								)}
							</Popover>
						</>
					)}
				</div>
			</header>

			<Card className="overflow-hidden">
				<div className="flex flex-wrap items-center gap-x-3 gap-y-3 px-4 py-3">
					<CountBlock label="Open" value={openCount} tone="open" icon={CircleDot} />
					<CountBlock
						label="Resolved"
						value={closedCount}
						tone="closed"
						icon={CircleCheck}
					/>
					{assignedToMe > 0 && (
						<CountBlock
							label="Waiting on you"
							value={assignedToMe}
							tone="open"
							icon={UserRound}
						/>
					)}
					<AllowanceBlock allowance={project.issue_allowance} />
					<div className="min-w-[10rem] flex-1">
						<SplitBar open={openCount} closed={closedCount} />
						<p className="mt-1.5 text-2xs text-ink-500">
							{issues.length === 0
								? "Nothing raised yet"
								: `${Math.round((closedCount / issues.length) * 100)}% of what has been raised is resolved`}
						</p>
						<AllowanceNote
							allowance={project.issue_allowance}
							isOwner={project.is_owner}
						/>
					</div>
					<Link
						to={`/projects/${projectId}/insights`}
						className="text-2xs font-medium text-brand-700 hover:text-brand-800 hover:underline"
					>
						See the full progress chart
					</Link>
				</div>

				<IngestAddress projectId={projectId} />
			</Card>

			{composing && (
				<IssueComposer
					projectId={projectId}
					tags={tags}
					canCreateTags={Boolean(project.is_owner)}
					onTagCreated={(tag) => setTags((current) => [...current, tag])}
					onCancel={() => setComposing(false)}
					onCreated={async (issue) => {
						setComposing(false)
						await afterMutation()
						toast(`Raised #${issue.number}`, "success")
					}}
				/>
			)}

			<div className="flex flex-wrap items-center gap-2">
				<div className="relative min-w-[12rem] flex-1 sm:max-w-xs">
					<Search
						size={14}
						className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-ink-400"
						aria-hidden
					/>
					<Input
						value={search}
						onChange={(event) => setSearch(event.target.value)}
						placeholder="Search issues"
						className="pl-8"
						aria-label="Search issues"
					/>
				</div>
				<ToggleGroup
					value={statusFilter}
					onChange={setStatusFilter}
					aria-label="Filter by status"
					options={[
						{ value: "open", label: "Open" },
						{ value: "closed", label: "Resolved" },
						{ value: "all", label: "All" }
					]}
				/>
				<Select
					value={assignedFilter}
					onChange={(event) => setAssignedFilter(event.target.value)}
					className="w-auto min-w-[9rem]"
					aria-label="Filter by assignment"
				>
					<option value="all">Anyone</option>
					<option value="me">Waiting on me</option>
					<option value="unassigned">Unassigned</option>
				</Select>
				<Select
					value={tagFilter}
					onChange={(event) => setTagFilter(event.target.value)}
					className="w-auto min-w-[9rem]"
					aria-label="Filter by tag"
				>
					<option value="all">All tags</option>
					<option value="untagged">Untagged</option>
					{tags.map((tag) => (
						<option key={tag.tag_id} value={tag.tag_id}>
							{tag.name} ({tag.issue_count})
						</option>
					))}
				</Select>
				<span className="ml-auto text-2xs text-ink-500">
					{plural(visible.length, "issue")} shown
				</span>
			</div>

			{visible.length === 0 ? (
				<EmptyState
					icon={issues.length ? Search : ListChecks}
					title={issues.length ? "Nothing matches those filters" : "No issues yet"}
					description={
						issues.length
							? "Try clearing the search or widening the filters."
							: project.is_member
								? "Raise the first clarification. Each one becomes a line item that can be answered and closed off."
								: "You have not been invited to any issues in this project yet."
					}
					action={
						issues.length ? (
							<Button
								onClick={() => {
									setSearch("")
									setStatusFilter("all")
									setTagFilter("all")
									setAssignedFilter("all")
								}}
							>
								Clear filters
							</Button>
						) : project.is_member && project.can_raise_issue !== false ? (
							<Button variant="primary" icon={Plus} onClick={() => setComposing(true)}>
								Raise the first issue
							</Button>
						) : null
					}
				/>
			) : (
				<ol className="space-y-2.5">
					{visible.map((issue) => (
						<IssueCard
							key={issue.issue_id}
							issue={issue}
							projectId={projectId}
							tagsById={tagsById}
							peopleById={peopleById}
							timezone={timezone}
							currentUserId={user?.user_id}
							busy={busyId === issue.issue_id}
							onToggle={() => toggleStatus(issue)}
						/>
					))}
				</ol>
			)}

			<ShareDialog
				open={sharing}
				onClose={() => setSharing(false)}
				projectId={projectId}
				projectName={project.name}
			/>

			<DeleteProjectDialog
				open={deleting}
				onClose={() => setDeleting(false)}
				project={project}
			/>
		</div>
	)
}

/**
 * The address that files straight into this project.
 *
 * Built from the project id rather than its name: the id is unambiguous, and
 * a name that reads clearly today collides the moment somebody starts
 * "Riverside Court - Phase 3". Forwarding a thread here has the questions in
 * it raised without anybody opening Loomina.
 *
 * Hidden entirely when the deployment has no ingest key configured, because
 * then the address would silently do nothing.
 */
function IngestAddress({ projectId }) {
	const { config, toast } = useApp()
	const [copied, setCopied] = useState(false)

	const base = config?.ingest_address
	if (!base || !base.includes("@")) return null

	const [mailbox, domain] = base.split("@")
	const address = `${mailbox}+${projectId}@${domain}`

	async function copy() {
		if (await copyText(address)) {
			setCopied(true)
			window.setTimeout(() => setCopied(false), 2000)
		} else {
			toast("Could not copy. Select the address and copy it by hand.", "error")
		}
	}

	return (
		<div className="flex flex-wrap items-center gap-x-3 gap-y-2 border-t border-ink-100 bg-ink-50/70 px-4 py-2.5">
			<Mail size={14} className="shrink-0 text-ink-400" aria-hidden />
			<span className="text-2xs text-ink-600">Create issues directly using AI by emailing</span>
			<code className="min-w-0 truncate rounded-md bg-white px-2 py-1 font-mono text-2xs text-ink-800 ring-1 ring-inset ring-ink-200">
				{address}
			</code>
			<Button
				size="sm"
				variant="ghost"
				icon={copied ? Check : Copy}
				onClick={copy}
				aria-label={`Copy ${address}`}
			>
				{copied ? "Copied" : "Copy"}
			</Button>
		</div>
	)
}

/**
 * How much room the project has left.
 *
 * The cap belongs to the project owner's plan rather than to whoever is
 * looking, because anyone on the project can raise an issue but only one
 * person is paying for it. Shown to everyone on the project, not just the
 * owner: someone about to write up eight clarifications should find out there
 * is room for two before they type them, not after.
 */
function AllowanceBlock({ allowance }) {
	if (!allowance) return null

	const { used, limit, unlimited, remaining, plan_name: planName, full } = allowance
	const skin = full
		? "border-rose-200 bg-rose-50/60 text-rose-800"
		: !unlimited && remaining <= 2
			? "border-amber-200 bg-amber-50/60 text-amber-800"
			: "border-ink-200 bg-ink-50/60 text-ink-800"

	return (
		<div className={cx("flex items-center gap-2.5 rounded-lg border px-3 py-1.5", skin)}>
			<Gauge size={22} className="shrink-0 opacity-80" aria-hidden />
			<span className="text-center">
				<span className="block text-lg font-semibold leading-none tabular">
					{unlimited ? used : `${used}/${limit}`}
				</span>
				<span className="block text-2xs text-ink-500">
					{unlimited ? `Issues · ${planName}` : `Issues · ${planName} plan`}
				</span>
			</span>
		</div>
	)
}

/** Said in words, and only once it matters. */
function AllowanceNote({ allowance, isOwner }) {
	if (!allowance || allowance.unlimited) return null
	if (!allowance.full && allowance.remaining > 2) return null

	const { limit, remaining, full } = allowance
	return (
		<p className={cx("mt-1 text-2xs font-medium", full ? "text-rose-700" : "text-amber-700")}>
			{full
				? `Full at ${limit} issues. `
				: `${plural(remaining, "issue")} left of ${limit}. `}
			{isOwner ? (
				<Link to="/settings/billing" className="underline hover:text-ink-900">
					Upgrade
				</Link>
			) : (
				"Ask the owner to upgrade"
			)}
			{", or close one that is settled."}
		</p>
	)
}

/**
 * One count, boxed.
 *
 * Each block carries its own tinted border rather than the three of them
 * sharing a row: open and resolved are two different readings of the same
 * project, and a shared surface invites them to be read as one running total.
 */
function CountBlock({ label, value, tone, icon: Icon }) {
	const tones = {
		open: "border-state-open/25 bg-state-open-soft/50 text-state-open-ink",
		closed: "border-state-closed/25 bg-state-closed-soft/50 text-state-closed-ink",
		neutral: "border-ink-200 bg-ink-50/60 text-ink-900"
	}
	const skin = tones[tone] || tones.neutral

	return (
		<div className={cx("flex items-center gap-2.5 rounded-lg border px-3 py-1.5", skin)}>
			{Icon && <Icon size={22} className="shrink-0 opacity-80" aria-hidden />}
			<span className="text-center">
				<span className="block text-lg font-semibold leading-none tabular">{value}</span>
				<span className="block text-2xs text-ink-500">{label}</span>
			</span>
		</div>
	)
}

/**
 * One issue.
 *
 * The header band is filled - the question itself is the thing anyone opening
 * this page is looking for, so it gets its own surface rather than sitting in
 * the same white as the metadata beneath it. The fill also carries the status,
 * which is why the whole row reads at a glance in a long list.
 */
function IssueCard({
	issue,
	projectId,
	tagsById,
	peopleById,
	timezone,
	currentUserId,
	busy,
	onToggle
}) {
	const closed = issue.status === "closed"
	const assignee = issue.assignee_id ? peopleById[issue.assignee_id] : null
	const waitingOnMe = issue.assignee_id === currentUserId && !closed

	const voices = [issue.created_by, ...(issue.comments || []).map((c) => c.author_id)]
	const contributors = [...new Set(voices.filter(Boolean))].slice(0, 4)
	const attachmentCount =
		(issue.attachments?.length || 0) +
		(issue.comments || []).reduce((sum, c) => sum + (c.attachments?.length || 0), 0)
	const replyCount = (issue.comments || []).filter(
		(c) => (c.kind || "comment") === "comment"
	).length

	return (
		<li
			className={cx(
				"overflow-hidden rounded-card border shadow-card transition",
				"hover:shadow-lifted",
				waitingOnMe
					? "border-state-open/40"
					: closed
						? "border-ink-200/70"
						: "border-ink-200/80 hover:border-ink-300"
			)}
		>
			{/* Filled header - the question, the status, and nothing else. */}
			<div
				className={cx(
					"flex items-start gap-3 px-3.5 py-3",
					closed ? "bg-state-closed-soft" : "bg-state-open-soft"
				)}
			>
				<button
					type="button"
					onClick={onToggle}
					disabled={busy}
					aria-label={
						closed ? `Reopen issue ${issue.number}` : `Resolve issue ${issue.number}`
					}
					className={cx(
						"mt-0.5 grid h-5 w-5 shrink-0 place-items-center rounded-full border-2 transition-colors",
						closed
							? "border-state-closed bg-state-closed text-white"
							: "border-state-open/40 bg-white text-transparent hover:border-state-closed hover:bg-state-closed hover:text-white",
						busy && "opacity-50"
					)}
				>
					<CircleCheck size={12} strokeWidth={3} aria-hidden />
				</button>

				<div className="min-w-0 flex-1">
					<div className="flex flex-wrap items-center gap-x-2 gap-y-1">
						<span
							className={cx(
								"shrink-0 text-2xs font-semibold tabular",
								closed ? "text-state-closed-ink/60" : "text-state-open-ink/60"
							)}
						>
							#{issue.number}
						</span>
						<Link
							to={`/projects/${projectId}/issues/${issue.issue_id}`}
							className={cx(
								"text-[0.9375rem] font-semibold leading-snug hover:underline",
								closed ? "text-state-closed-ink" : "text-state-open-ink"
							)}
						>
							{issue.title}
						</Link>
					</div>
				</div>

				<StatusBadge status={issue.status} size="sm" />
			</div>

			{/* Metadata band */}
			<div className="flex flex-wrap items-center gap-x-3 gap-y-2 bg-white px-3.5 py-2.5">
				{(issue.tag_ids || []).map((tagId) => (
					<TagChip key={tagId} tag={tagsById[tagId]} size="sm" />
				))}

				{assignee && !closed && (
					<span
						className={cx(
							"inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-2xs font-medium ring-1 ring-inset",
							waitingOnMe
								? "bg-state-open-soft text-state-open-ink ring-state-open/30"
								: "bg-ink-100 text-ink-700 ring-ink-200"
						)}
						title={`Assigned ${relativeTime(issue.assigned_at, timezone)}`}
					>
						<UserRound size={11} aria-hidden />
						{waitingOnMe ? "Waiting on you" : `Waiting on ${assignee.name}`}
					</span>
				)}

				<span className="flex items-center gap-1 text-2xs text-ink-500">
					<MessageSquare size={11} aria-hidden />
					{replyCount}
				</span>

				{attachmentCount > 0 && (
					<span className="flex items-center gap-1 text-2xs text-ink-500">
						<Paperclip size={11} aria-hidden />
						{attachmentCount}
					</span>
				)}

				<span className="text-2xs text-ink-400">
					Updated {relativeTime(issue.updated_at, timezone)}
				</span>

				<div className="ml-auto flex shrink-0 -space-x-1.5">
					{contributors.map((id) => (
						<Avatar
							key={id}
							person={peopleById[id]}
							size="xs"
							className="ring-2 ring-white"
						/>
					))}
				</div>
			</div>
		</li>
	)
}
